import { Action } from '@ngrx/store';
import { Reference } from '../../utils/action-helper.util';
import { Error } from '../_models/auth.models';
import {
  Role,
  UpdateActivePayload,
  UpdateEmailAlertsPayload,
  UpdateProfilePayload,
  UserProfile,
} from '../_models/user.models';

/**
 * Auth action types
 */
export enum UserActionTypes {
  PROFILE = '[User] Login',
  PROFILE_SUCCESS = '[User] Login Success',
  PROFILE_FAILED = '[User] Login Failed',
  UPDATE_PROFILE = '[User] Update Profile',
  UPDATE_PROFILE_SUCCESS = '[User] Update Profile Success',
  UPDATE_PROFILE_FAILED = '[User] Update Profile Failed',
  UPDATE_ACTIVE = '[User] Update Active',
  UPDATE_ACTIVE_SUCCESS = '[User] Update Active Success',
  UPDATE_ACTIVE_FAILED = '[User] Update Active Failed',
  TOGGLE_EMAIL_ALERTS = '[User] Toggle Email Alerts',
  TOGGLE_EMAIL_ALERTS_SUCCESS = '[User] Toggle Email Alerts Success',
  TOGGLE_EMAIL_ALERTS_FAILED = '[User] Toggle Email Alerts Failed',
  LOAD_APPLICATION_ROLES = '[Field] Load Application Roles',
  LOAD_APPLICATION_ROLES_SUCCESS = '[Field] Load Application Roles Success',
  LOAD_APPLICATION_ROLES_FAILED = '[Field] Load Application Roles Failed',
  LOAD_USERS = '[Field] Load Users',
  LOAD_USERS_SUCCESS = '[Field] Load Users Success',
  LOAD_USERS_FAILED = '[Field] Load Users Failed',
  LOAD_DEFAULT_PAYMENT_METHOD = '[User] Load Default Payment Method',
  LOAD_DEFAULT_PAYMENT_METHOD_SUCCESS = '[User] Load Default Payment Method Success',
  LOAD_DEFAULT_PAYMENT_METHOD_FAILED = '[User] Load Default Payment Method Failed',
  LOAD_CURRENT_PLAN = '[User] Load Current Plan',
  LOAD_CURRENT_PLAN_SUCCESS = '[User] Load Current Plan Success',
  LOAD_CURRENT_PLAN_FAILED = '[User] Load Current Plan Failed',
  LOAD_ALL_PLANS = '[User] Load All Plans',
  LOAD_ALL_PLANS_SUCCESS = '[User] Load All Plans Success',
  LOAD_ALL_PLANS_FAILED = '[User] Load All Plans Failed',
  LOAD_STRIPE_PORTAL = '[User] Load Stripe Portal',
  LOAD_STRIPE_PORTAL_SUCCESS = '[User] Load Stripe Portal Success',
  LOAD_STRIPE_PORTAL_FAILED = '[User] Load Stripe Portal Failed',
  SUBSCRIBE_PLAN = '[User] Subscribe Plan',
  SUBSCRIBE_PLAN_SUCCESS = '[User] Subscribe Plan Success',
  SUBSCRIBE_PLAN_FAILED = '[User] Subscribe Plan Failed',
  VALIDATE_USER_EMAIL = '[User] Validate User Email',
  VALIDATE_USER_EMAIL_SUCCESS = '[User] Validate User Email Success',
  VALIDATE_USER_EMAIL_FAILED = '[User] Validate User Email Failed',
  UPDATE_USER_EMAIL = '[User] Update User Email',
  UPDATE_USER_EMAIL_SUCCESS = '[User] Update User Email Success',
  UPDATE_USER_EMAIL_FAILED = '[User] Update User Email Failed',
  BULK_UPDATE_USERS = '[User] Bulk Update Users',
  BULK_UPDATE_USERS_SUCCESS = '[User] Bulk Update Users Success',
  BULK_UPDATE_USERS_FAILED = '[User] Bulk Update Users Failed',
  RESET = '[User] RESET',
}

/**
 * Operations
 */
export class Profile implements Action {
  readonly type = UserActionTypes.PROFILE;

  /**
   * @param applicationKey Optional application key to get entity-scoped role
   */
  constructor(public applicationKey?: string) {}
}

export class ProfileSuccess implements Action {
  readonly type = UserActionTypes.PROFILE_SUCCESS;

  constructor(public payload: UserProfile, public applicationKey?: string) {}
}

export class ProfileFailed implements Action {
  readonly type = UserActionTypes.PROFILE_FAILED;

  constructor(public payload: Error) {}
}

export class UpdateProfile extends Reference implements Action {
  readonly type = UserActionTypes.UPDATE_PROFILE;

  constructor(public payload: UpdateProfilePayload) {
    super();
  }
}

export class UpdateProfileSuccess extends Reference implements Action {
  readonly type = UserActionTypes.UPDATE_PROFILE_SUCCESS;

  constructor(public payload: UserProfile) {
    super();
  }
}

export class UpdateProfileFailed extends Reference implements Action {
  readonly type = UserActionTypes.UPDATE_PROFILE_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class UpdateActive extends Reference implements Action {
  readonly type = UserActionTypes.UPDATE_ACTIVE;

  constructor(public payload: UpdateActivePayload) {
    super();
  }
}

export class UpdateActiveSuccess extends Reference implements Action {
  readonly type = UserActionTypes.UPDATE_ACTIVE_SUCCESS;

  constructor(public payload: UserProfile) {
    super();
  }
}

export class UpdateActiveFailed extends Reference implements Action {
  readonly type = UserActionTypes.UPDATE_ACTIVE_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class ToggleEmailAlerts extends Reference implements Action {
  readonly type = UserActionTypes.TOGGLE_EMAIL_ALERTS;

  constructor(public payload: UpdateEmailAlertsPayload) {
    super();
  }
}

export class ToggleEmailAlertsSuccess extends Reference implements Action {
  readonly type = UserActionTypes.TOGGLE_EMAIL_ALERTS_SUCCESS;

  constructor(public payload: UserProfile) {
    super();
  }
}

export class ToggleEmailAlertsFailed extends Reference implements Action {
  readonly type = UserActionTypes.TOGGLE_EMAIL_ALERTS_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class LoadApplicationRoles implements Action {
  readonly type = UserActionTypes.LOAD_APPLICATION_ROLES;

  /**
   * @param force to forcefully fetch, regardless of caching enabled
   */
  constructor(
    public applicationId: number,
    public tableId: string = '',
    public fromForm: boolean = false,
    public force: boolean = false
  ) {}
}

export class LoadApplicationRolesSuccess implements Action {
  readonly type = UserActionTypes.LOAD_APPLICATION_ROLES_SUCCESS;

  constructor(public applicationId: number, public payload: Array<Role>) {}
}

export class LoadApplicationRolesFailed implements Action {
  readonly type = UserActionTypes.LOAD_APPLICATION_ROLES_FAILED;

  constructor(public payload: Error, public applicationId: number) {}
}

export class LoadUsers implements Action {
  readonly type = UserActionTypes.LOAD_USERS;

  constructor(
    public applicationId: number,
    public isSearch: boolean,
    public search: string,
    public isScroll: boolean,
    public skip: number,
    public pageSize: number,
    public isOptimizedComponent: boolean,
    public force: boolean = false
  ) {}
}

export class LoadUsersSuccess implements Action {
  readonly type = UserActionTypes.LOAD_USERS_SUCCESS;

  constructor(
    public applicationId: number,
    public payload: Array<UserProfile>
  ) {}
}

export class LoadUsersFailed implements Action {
  readonly type = UserActionTypes.LOAD_USERS_FAILED;

  constructor(public payload: Error) {}
}

export class LoadDefaultPaymentMethod implements Action {
  readonly type = UserActionTypes.LOAD_DEFAULT_PAYMENT_METHOD;

  constructor(public customerId: string) {}
}

export class LoadDefaultPaymentMethodSuccess implements Action {
  readonly type = UserActionTypes.LOAD_DEFAULT_PAYMENT_METHOD_SUCCESS;

  constructor(public payload: any) {}
}

export class LoadDefaultPaymentMethodFailed implements Action {
  readonly type = UserActionTypes.LOAD_DEFAULT_PAYMENT_METHOD_FAILED;

  constructor(public payload: Error) {}
}

export class LoadCurrentPlan implements Action {
  readonly type = UserActionTypes.LOAD_CURRENT_PLAN;

  constructor(public force: boolean = false) {}
}

export class LoadCurrentPlanSuccess implements Action {
  readonly type = UserActionTypes.LOAD_CURRENT_PLAN_SUCCESS;

  constructor(public payload: any) {}
}

export class LoadCurrentPlanFailed implements Action {
  readonly type = UserActionTypes.LOAD_CURRENT_PLAN_FAILED;

  constructor(public payload: Error) {}
}

export class LoadAllPlans implements Action {
  readonly type = UserActionTypes.LOAD_ALL_PLANS;

  constructor(public force: boolean = false) {}
}

export class LoadAllPlansSuccess implements Action {
  readonly type = UserActionTypes.LOAD_ALL_PLANS_SUCCESS;

  constructor(public payload: any) {}
}

export class LoadAllPlansFailed implements Action {
  readonly type = UserActionTypes.LOAD_ALL_PLANS_FAILED;

  constructor(public payload: Error) {}
}

export class LoadStripePortal implements Action {
  readonly type = UserActionTypes.LOAD_STRIPE_PORTAL;

  constructor(public force: boolean = false) {}
}

export class LoadStripePortalSuccess implements Action {
  readonly type = UserActionTypes.LOAD_STRIPE_PORTAL_SUCCESS;

  constructor(public payload: any) {}
}

export class LoadStripePortalFailed implements Action {
  readonly type = UserActionTypes.LOAD_STRIPE_PORTAL_FAILED;

  constructor(public payload: Error) {}
}

export class SubscribePlan implements Action {
  readonly type = UserActionTypes.SUBSCRIBE_PLAN;

  constructor(public force: boolean = false) {}
}

export class SubscribePlanSuccess extends Reference implements Action {
  readonly type = UserActionTypes.SUBSCRIBE_PLAN_SUCCESS;

  constructor(public payload: any) {
    super();
  }
}

export class SubscribePlanFailed extends Reference implements Action {
  readonly type = UserActionTypes.SUBSCRIBE_PLAN_FAILED;

  constructor(public payload: Error) {
    super();
  }
}
export class ValidateUserEmail implements Action {
  readonly type = UserActionTypes.VALIDATE_USER_EMAIL;

  constructor(public payload: any) {}
}

export class ValidateUserEmailSuccess extends Reference implements Action {
  readonly type = UserActionTypes.VALIDATE_USER_EMAIL_SUCCESS;

  constructor(public payload: any) {
    super();
  }
}

export class ValidateUserEmailFailed extends Reference implements Action {
  readonly type = UserActionTypes.VALIDATE_USER_EMAIL_FAILED;

  constructor(public payload: Error) {
    super();
  }
}
export class UpdateUserEmail implements Action {
  readonly type = UserActionTypes.UPDATE_USER_EMAIL;

  constructor(public payload: any) {}
}

export class UpdateUserEmailSuccess extends Reference implements Action {
  readonly type = UserActionTypes.UPDATE_USER_EMAIL_SUCCESS;

  constructor(public payload: any) {
    super();
  }
}

export class UpdateUserEmailFailed extends Reference implements Action {
  readonly type = UserActionTypes.UPDATE_USER_EMAIL_FAILED;

  constructor(public payload: Error) {
    super();
  }
}
export class BulkUpdateUsers extends Reference implements Action {
  readonly type = UserActionTypes.BULK_UPDATE_USERS;

  constructor(public payload: any) {
    super()
  }
}

export class BulkUpdateUsersSuccess extends Reference implements Action {
  readonly type = UserActionTypes.BULK_UPDATE_USERS_SUCCESS;

  constructor(public payload: any) {
    super();
  }
}

export class BulkUpdateUsersFailed extends Reference implements Action {
  readonly type = UserActionTypes.BULK_UPDATE_USERS_FAILED;

  constructor(public payload: Error) {
    super();
  }
}

export class Reset extends Reference implements Action {
  readonly type = UserActionTypes.RESET;

  constructor() {
    super();
  }
}

/**
 * Available types
 */
export type Actions =
  | Profile
  | ProfileSuccess
  | ProfileFailed
  | UpdateProfile
  | UpdateProfileSuccess
  | UpdateProfileFailed
  | UpdateActive
  | UpdateActiveSuccess
  | UpdateActiveFailed
  | LoadApplicationRoles
  | LoadApplicationRolesSuccess
  | LoadApplicationRolesFailed
  | LoadUsers
  | LoadUsersSuccess
  | LoadUsersFailed
  | LoadCurrentPlan
  | LoadCurrentPlanSuccess
  | LoadCurrentPlanFailed
  | LoadAllPlans
  | LoadAllPlansSuccess
  | LoadAllPlansFailed
  | LoadStripePortal
  | LoadStripePortalSuccess
  | LoadStripePortalFailed
  | SubscribePlan
  | SubscribePlanSuccess
  | SubscribePlanFailed
  | ValidateUserEmail
  | ValidateUserEmailSuccess
  | ValidateUserEmailFailed
  | UpdateUserEmail
  | UpdateUserEmailSuccess
  | UpdateUserEmailFailed
  | BulkUpdateUsers
  | BulkUpdateUsersSuccess
  | BulkUpdateUsersFailed
  | ToggleEmailAlerts
  | ToggleEmailAlertsSuccess
  | ToggleEmailAlertsFailed
  | LoadDefaultPaymentMethod
  | LoadDefaultPaymentMethodSuccess
  | LoadDefaultPaymentMethodFailed
  | Reset;
